import type { z } from 'zod'

import { DEFAULT_DATE_RANGE } from '~publish/pages/Community/CommunityFilters/types'

import { CALENDAR_STATUS_URL_TO_CANONICAL } from './calendarStatusMap'
import { validateFilterShape } from './filterShapes'
import type { SectionFilters } from './filterShapes'
import type { SavedViewSection } from './types'

/**
 * The date range an Insights page resolves to when the URL omits one. Capture
 * stores it so the default view round-trips, and `findMatchingSavedView`
 * applies the same default so a view saved without an explicit range still
 * matches. Both must agree, so they share this constant.
 */
export const INSIGHTS_DEFAULT_DATE_RANGE = 'last-30-days'

function getArrayParam(
  searchParams: URLSearchParams,
  key: string,
): string[] | undefined {
  // `?channels[]=` would otherwise produce a `['']` entry that Zod's
  // ObjectIdHexStringSchema rejects later with a confusing error. Drop empty
  // entries so a malformed URL never persists invalid IDs into a saved view.
  const bracketed = searchParams
    .getAll(`${key}[]`)
    .filter((entry) => entry.length > 0)
  if (bracketed.length > 0) return bracketed
  const bare = searchParams.getAll(key).filter((entry) => entry.length > 0)
  if (bare.length > 0) return bare
  return undefined
}

function getChannelIdFromCommunityPath(pathname: string): string | undefined {
  const match = pathname.match(/^\/channels\/([^/]+)\/(?:community|comments)/)
  return match?.[1]
}

function getChannelIdFromPublishPath(pathname: string): string | undefined {
  const match = pathname.match(/^\/channels\/([^/]+)(?:\/|$)/)
  return match?.[1]
}

function getChannelIdFromInsightsPath(pathname: string): string | undefined {
  const match = pathname.match(/^\/(?:insights|analytics)\/([^/?#]+)/)
  return match?.[1]
}

function getCalendarGranularityFromPath(pathname: string): string | undefined {
  const match = pathname.match(/\/calendar\/([^/?#]+)/)
  return match?.[1]
}

function isCalendarPath(pathname: string): boolean {
  return /\/calendar(\/|$)/.test(pathname)
}

/**
 * Result of capturing filters from a URL.
 *
 * `ok: true` carries the validated, canonical-key `SectionFilters` payload
 * (e.g. `channelIds`, `tagIds`, `dateRange`) — ready to round-trip through
 * `buildUrlFromSavedView` and to send straight to the create mutation.
 *
 * `ok: false` carries the Zod issues so callers can render a per-field error
 * region and block the save. The issues come from the section-specific
 * schema in `./filterShapes`, so they identify exactly which key is
 * malformed without exposing the schema instance.
 */
export type CaptureFiltersResult =
  | { ok: true; filters: SectionFilters }
  | { ok: false; issues: z.core.$ZodIssue[] }

// Captured filter keys that mirror route shape rather than user-applied
// filtering. Excluded from `hasCapturedFilters` so a Calendar URL with no
// real query filter beyond the granularity path segment is not treated as
// having something to save.
const PATH_ONLY_FILTER_KEYS = new Set(['granularity'])

/**
 * Reports whether a captured-filter payload contains at least one entry that
 * came from a URL query param (versus only path-derived state). Use this to
 * gate "is there a filter worth saving?" for sections without a view selector
 * (insights), where the view slug alone can't make the route saveable.
 *
 * Example: on `/schedule/calendar/week?status=all`, `captureFilters` drops
 * `'all'` (no filter) and returns `{ granularity: 'week' }` — the path-only
 * `granularity` is excluded here, so the payload has no user filter to save.
 */
export function hasCapturedFilters(filters: Record<string, unknown>): boolean {
  return Object.keys(filters).some((key) => !PATH_ONLY_FILTER_KEYS.has(key))
}

function buildPublishFilters(
  pathname: string,
  searchParams: URLSearchParams,
): Record<string, unknown> {
  const filters: Record<string, unknown> = {}

  const queryChannels = getArrayParam(searchParams, 'channels')
  const pathChannel = getChannelIdFromPublishPath(pathname)
  if (queryChannels) {
    filters.channelIds = queryChannels
  } else if (pathChannel) {
    filters.channelIds = [pathChannel]
  }

  const tags = getArrayParam(searchParams, 'tags')
  if (tags) filters.tagIds = tags

  const date = searchParams.get('date')
  if (date) filters.date = date

  if (isCalendarPath(pathname)) {
    const rawStatus = searchParams.get('status')
    if (rawStatus) {
      const canonical = CALENDAR_STATUS_URL_TO_CANONICAL[rawStatus]
      if (canonical) filters.status = canonical
    }
    filters.granularity = getCalendarGranularityFromPath(pathname) ?? 'week'
  }
  // List paths intentionally do not read `?status=` — the tab lives in
  // `savedView.view` via `deriveViewFromRoute`, and the List UI never emits
  // `?status=` organically.

  return filters
}

// The community page emits its date-range filter as `publishedAt=last7days`
// in the URL (legacy form). The canonical storage shape, shared with insights,
// is kebab-case (`dateRange: 'last-7-days'`). This map translates one to the
// other so the URL stays backwards-compatible while the backend never sees
// the legacy form. Keep in sync with `COMMUNITY_DATE_RANGE_CANONICAL_TO_URL`
// in `./buildUrlFromSavedView.ts`.
const COMMUNITY_DATE_RANGE_URL_TO_CANONICAL: Record<string, string> = {
  last7days: 'last-7-days',
  last30days: 'last-30-days',
  last6months: 'last-6-months',
  lastyear: 'last-year',
}

/** Matching and URL building must agree on this, so they share it. */
export const COMMUNITY_DEFAULT_DATE_RANGE =
  COMMUNITY_DATE_RANGE_URL_TO_CANONICAL[DEFAULT_DATE_RANGE]

const COMMUNITY_CANONICAL_DATE_RANGES = new Set(
  Object.values(COMMUNITY_DATE_RANGE_URL_TO_CANONICAL),
)

export const isCommunityCanonicalDateRange = (value: unknown): boolean =>
  typeof value === 'string' && COMMUNITY_CANONICAL_DATE_RANGES.has(value)

function buildCommunityFilters(
  pathname: string,
  searchParams: URLSearchParams,
): Record<string, unknown> {
  const filters: Record<string, unknown> = {}

  const queryChannels = getArrayParam(searchParams, 'channels')
  const pathChannel = getChannelIdFromCommunityPath(pathname)
  if (queryChannels) {
    filters.channelIds = queryChannels
  } else if (pathChannel) {
    filters.channelIds = [pathChannel]
  }

  const status = searchParams.get('status')
  if (status) filters.status = status

  // `publishedAt` is the legacy URL param name. Translate the value from the
  // community page's legacy format (`last7days`) to the canonical kebab-case
  // shape (`last-7-days`) — the backend rejects the legacy form. Unknown
  // values pass through so the Zod schema can produce a useful error.
  const publishedAt = searchParams.get('publishedAt')
  if (publishedAt) {
    filters.dateRange =
      COMMUNITY_DATE_RANGE_URL_TO_CANONICAL[publishedAt] ??
      COMMUNITY_DATE_RANGE_URL_TO_CANONICAL[DEFAULT_DATE_RANGE]
  }

  const sort = searchParams.get('sort')
  if (sort) filters.sort = sort

  return filters
}

function buildCreateFilters(
  searchParams: URLSearchParams,
): Record<string, unknown> {
  const filters: Record<string, unknown> = {}

  const tagIds = getArrayParam(searchParams, 'tagIds')
  if (tagIds) filters.tagIds = tagIds

  const groupIds = getArrayParam(searchParams, 'groupIds')
  if (groupIds) filters.groupIds = groupIds

  return filters
}

function buildInsightsFilters(
  pathname: string,
  searchParams: URLSearchParams,
): Record<string, unknown> {
  const filters: Record<string, unknown> = {}

  const pathChannel = getChannelIdFromInsightsPath(pathname)
  if (pathChannel) {
    filters.channelIds = [pathChannel]
  } else {
    const queryChannels = getArrayParam(searchParams, 'channels')
    if (queryChannels) filters.channelIds = queryChannels
  }

  // Insights always has an effective date range — the page falls back to
  // last-30-days when no param is present. Capturing that default lets the bare
  // page round-trip and makes the default view saveable like any other.
  filters.dateRange =
    searchParams.get('dateRange') ?? INSIGHTS_DEFAULT_DATE_RANGE

  // Both `startDate` and `endDate` participate in `dateRange=custom` — see
  // `useDateRangeSelector`. Capture both so a saved custom range round-trips.
  const startDate = searchParams.get('startDate')
  if (startDate) filters.startDate = startDate

  const endDate = searchParams.get('endDate')
  if (endDate) filters.endDate = endDate

  // The default comparison (`previous-period`) is omitted from the URL, so only
  // a non-default selection reaches capture. Persisting it lets a saved view
  // restore the comparison instead of silently dropping it on reopen.
  const comparison = searchParams.get('comparison')
  if (comparison) filters.comparison = comparison

  return filters
}

/**
 * Extracts the canonical filter payload for a saved view from the pathname
 * and query string, then validates it against the section-specific Zod
 * schema in `./filterShapes`.
 *
 * Returns a discriminated union: callers must branch on `ok` before reading
 * `filters`. Validation failures carry the `ZodIssue[]` array so the dialog
 * can surface exactly which key was rejected — no silent truncation, no
 * malformed payload reaching the mutation.
 *
 * Empty arrays are dropped before validation because `.min(1)` rejects them;
 * absence is the correct "no filter" representation in this schema.
 */
export function captureFilters(
  section: SavedViewSection,
  pathname: string,
  searchParams: URLSearchParams,
): CaptureFiltersResult {
  const filters = ((): Record<string, unknown> => {
    switch (section) {
      case 'publish':
        return buildPublishFilters(pathname, searchParams)
      case 'community':
        return buildCommunityFilters(pathname, searchParams)
      case 'create':
        return buildCreateFilters(searchParams)
      case 'insights':
        return buildInsightsFilters(pathname, searchParams)
      default: {
        const exhaustiveCheck: never = section
        return exhaustiveCheck
      }
    }
  })()

  const result = validateFilterShape(section, filters)
  if (result.success) {
    return { ok: true, filters: result.data }
  }
  return { ok: false, issues: result.error.issues }
}
